import {
  Button,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  useDisclosure,
  Text as ChakraText,
  chakra,
} from '@chakra-ui/react'
import React, { useEffect, useRef, useCallback, useContext } from 'react'
import type { DropzoneOptions } from 'dropzone'
import Dropzone from 'dropzone'
import { Trans, useTranslation } from 'react-i18next'
import { MLContext } from '../../../providers'

interface Props {
  isModalOpen: boolean
  closeModal: Function
  onFilesDrop: Function | undefined
  onFileOrDirectoryDrop: Function | undefined
}

let dropzoneInstance: Dropzone | undefined

const FolderUpload = ({
  isModalOpen,
  closeModal,
  onFilesDrop,
  onFileOrDirectoryDrop,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const { isOpen, onClose, onOpen } = useDisclosure()
  const firstRender = useRef(false)
  const [{ folderPath }] = useContext(MLContext)

  const closeFolderUploadModal = (): void => {
    onClose()
    closeModal()
  }

  useEffect(() => {
    if (isModalOpen) {
      onOpen()
    }
  }, [isModalOpen])

  const initializeDropzone = useCallback(
    (node: HTMLButtonElement | null) => {
      const btnId = node?.id ? node.id : null
      let dropEventCalled = false // var to distinghuish between drop event and files-added event

      if (dropzoneInstance) {
        dropzoneInstance.destroy()
      } // destroy previous dropzone instance if any

      const dropzoneOptions: DropzoneOptions = {
        url: 'some_random_url', // will never be used
        previewsContainer: false,
        autoProcessQueue: false,
        clickable: btnId ? `#${btnId}` : false,
        init() {
          if (btnId) {
            // eslint-disable-next-line
            this.hiddenFileInput?.setAttribute('webkitdirectory', 'true')
          }
        },
      }

      const myDropZone = new Dropzone('body', dropzoneOptions)

      //
      // drop event is called whenever files/directories are dropped
      // (which in turn also calls files-added event)
      //
      // files-added event is called when files are added through any means
      // (drop or by html input)
      //
      // files added through input are handled sperately as it used webkitdirectory,
      // in case of drop we have to iterate all files and directoris to get each file object and its relative path
      //

      const handleDrop = async (event): Promise<void> => {
        dropEventCalled = true
        if (
          onFileOrDirectoryDrop
          // &&
          // (window.currentMenuItem == 'media-library' ||
          //   window.isMediaLibraryWidget)
        ) {
          await onFileOrDirectoryDrop(event)
        }
      }

      myDropZone.on('drop', event => {
        void handleDrop(event)
      })

      myDropZone.on('addedfiles', files => {
        if (
          onFilesDrop
          // &&
          // (window.currentMenuItem == 'media-library' ||
          //   window.isMediaLibraryWidget)
        ) {
          if (!dropEventCalled) {
            onFilesDrop(files)
            closeFolderUploadModal()
          }
        }
      })

      dropzoneInstance = myDropZone
    },
    [onFilesDrop, onFileOrDirectoryDrop],
  )

  useEffect(() => {
    if ( onFilesDrop && onFileOrDirectoryDrop) {
      initializeDropzone(null) // initialize dropzone only on first render
      firstRender.current = true
    }
  }, [onFilesDrop, onFileOrDirectoryDrop])

  return (
    <Modal isOpen={isOpen} onClose={closeFolderUploadModal}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{t('upload_folder', 'Upload Folder')}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Trans t={t} i18nKey="upload_folder_description">
            <ChakraText color="appBattleshipGrey" maxWidth="100%">
              The contents of the selected folder will be uploaded under{' '}
              <chakra.span color="appDarkIndigo" fontWeight="medium">
                Home{{ folderPath }}
              </chakra.span>
              .
            </ChakraText>
          </Trans>
        </ModalBody>
        <ModalFooter borderTop="1px solid #e7eaf0" style={{ gap: '1em' }}>
          <Button onClick={closeFolderUploadModal} variant="outline">
            {t('cancel', 'Cancel')}
          </Button>
          <Button id="folder-upload-btn" ref={initializeDropzone}>
            {t('select_folders_to_upload', 'Select folders to upload')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}

export default FolderUpload
